import { FormField } from '@forms/formField';
import { RootState } from '@iwProvider/configureStore';
import { useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { defineMessage, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { reduxForm, formValueSelector, InjectedFormProps } from 'redux-form';

import { BillingPostcodeQuestion } from './BillingPostcodeQuestion';
import { CardholderNameQuestion } from './CardholderNameQuestion';
import { CardNumberQuestion } from './CardNumberQuestion';
import { CVVQuestion } from './CVVQuestion';
import { ExpiryDateQuestion } from './ExpiryDateQuestion';
import { validatePostcode, required } from '../../../forms/helpers/validation';
import {
    REGISTER_DEBIT_CARD,
    HANDLE_STRIPE_FIELD_CHANGE,
    SAVE_CARD_NUMBER_ELEMENT
} from '../../modals/addPaymentMethodModal/AddPaymentMethodModal.actions';
import ConfirmationButton from '../../ui/confirmationButton/ConfirmationButton';
import styles from '../LinkDebitCard.module.css';

const billingPostcodeErrorMessage = defineMessage({
    id: '_.link_debit_card.billing_postcode_error',
    defaultMessage: 'Invalid postcode'
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    saveCardNumberElement: (cardNumberElement: StripeCardNumberElement) =>
        dispatch({
            type: SAVE_CARD_NUMBER_ELEMENT,
            cardNumberElement
        }),
    createToken: (
        stripe: Stripe,
        repaymentRole: string | null,
        billingPostcode: string,
        cardholderName: string
    ) =>
        dispatch({
            type: REGISTER_DEBIT_CARD,
            stripe,
            repaymentRole,
            billingPostcode,
            cardholderName
        }),
    handleFieldChange: (field: string, response: any) =>
        dispatch({
            type: HANDLE_STRIPE_FIELD_CHANGE,
            field: field,
            response: response
        })
});

const formConfiguration = {
    form: 'linkDebitCard',
    validate: (props: any) => ({
        billingPostcode: validatePostcode(
            billingPostcodeErrorMessage,
            props.billingPostcode
        ),
        cardholderName: required(props.cardholderName)
    })
};

const selector = formValueSelector('linkDebitCard');

const mapStateToProps = (state: RootState) => ({
    ...state.addPaymentMethodModal,
    billingPostcode: selector(state, 'billingPostcode'),
    cardholderName: selector(state, 'cardholderName'),
    initialValues: {
        repaymentRole: true
    }
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const LinkDebitCardForm = ({
    invalidCardType,
    invalidCard,
    invalidNumber,
    invalidCVV,
    invalidExpiryDate,
    linkingDebitCard,
    handleSubmit,
    registerCardStatus,
    createToken,
    billingPostcode,
    cardholderName,
    saveCardNumberElement,
    handleFieldChange,
    showSubText = true,
    showDefaultPaymentMethodCheckbox = false
}: {
    linkingDebitCard?: boolean;
    showSubText?: boolean;
    showDefaultPaymentMethodCheckbox?: boolean;
} & PropsFromRedux &
    InjectedFormProps) => {
    const handleChange = (field: string) => (response: any) => {
        handleFieldChange(field, response);
    };

    const disabledStates = [
        invalidCardType,
        invalidCard,
        invalidNumber,
        invalidCVV,
        invalidExpiryDate
    ];

    const stripe = useStripe();
    return (
        <form
            data-testid="link-debit-card-modal"
            className={styles.LinkDebitCardForm}
            id="payment-form"
            onSubmit={handleSubmit(
                ({
                    billingPostcode,
                    cardholderName,
                    repaymentRole
                }: {
                    billingPostcode?: string;
                    cardholderName?: string;
                    repaymentRole?: string;
                }) =>
                    createToken(
                        stripe!,
                        repaymentRole ? 'primary' : null,
                        billingPostcode!,
                        cardholderName!
                    )
            )}
        >
            {showSubText && (
                <div className={styles.subText}>
                    <FormattedMessage
                        tagName="p"
                        id="_.link_debit_card.scheduled_repayments_text"
                        defaultMessage="Your scheduled repayments will come from this debit card."
                    />
                </div>
            )}

            <div className={styles.topRow}>
                <CardNumberQuestion
                    onReady={(element) => saveCardNumberElement(element)}
                    error={{
                        invalidNumber,
                        invalidCard,
                        invalidCardType
                    }}
                    onChange={handleChange('cardNumber')}
                />
                <ExpiryDateQuestion
                    error={invalidExpiryDate}
                    onChange={handleChange('expiryDate')}
                />
                <CVVQuestion
                    error={invalidCVV}
                    onChange={handleChange('CVV')}
                />
                <BillingPostcodeQuestion />
                <CardholderNameQuestion />
            </div>

            {showDefaultPaymentMethodCheckbox && (
                <div className="mb-2xl">
                    <FormField
                        name="repaymentRole"
                        type="checkbox"
                        label={
                            <FormattedMessage
                                id="_.link_debit_card.repayment_role_label"
                                defaultMessage="Use as default payment method for automatic repayments"
                            />
                        }
                    />
                </div>
            )}

            {registerCardStatus ? (
                <FormattedMessage
                    id="_.link_debit_card.successful_link"
                    defaultMessage="Thanks! You've successfully linked your card! Continue to the next step"
                />
            ) : null}

            <div className={styles.confirmationButtonContainer}>
                <ConfirmationButton
                    type="submit"
                    status={registerCardStatus}
                    disabled={
                        !billingPostcode ||
                        !cardholderName ||
                        disabledStates.some((element) => element === true)
                    }
                    loading={linkingDebitCard}
                    className={styles.confirmationButton}
                >
                    <FormattedMessage
                        id="_.link_debit_card.link_card"
                        defaultMessage="Add your card"
                    />
                </ConfirmationButton>
            </div>
        </form>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(
    reduxForm<
        object,
        {
            linkingDebitCard?: boolean;
            showSubText?: boolean;
            showDefaultPaymentMethodCheckbox?: boolean;
        } & PropsFromRedux
    >(formConfiguration)(LinkDebitCardForm)
);
